import { LoadingButton } from "@mui/lab";
import { Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useState } from "react";

import { CommentListFragment } from "../../generated-graphql/graphql";
import { useCreateTierIIReportComment } from "../../hooks/comments";
import { useFulfillmentDetails } from "../../routes/Staff/Fulfillment/FulfillmentDetails/useFulfillmentDetails";
import { CommentList } from "./CommentList";

export interface WorkflowCommentsProps {
  workflowId?: string | null;
  comments?: CommentListFragment[] | null;
  reportId?: string | null; // For fulfillment compatibility
  onRefetch?: () => void;
  loading?: boolean;
  disabled?: boolean;
  buttonText?: string; // "Comment" vs "Submit"
  title?: string;
}

export function WorkflowComments({
  workflowId,
  comments,
  reportId,
  onRefetch,
  loading: externalLoading = false,
  disabled = false,
  buttonText = "Comment",
  title = "Workflow Comments",
}: WorkflowCommentsProps) {
  const theme = useTheme();
  const [createComment, { loading: isUpsertCommentLoading }] = useCreateTierIIReportComment();

  const [newComment, setNewComment] = useState("");

  const loading = isUpsertCommentLoading || externalLoading;

  const handleAddComment = async () => {
    if (!newComment.trim() || !workflowId) return;

    await createComment({
      variables: {
        input: {
          reportId,
          workflowId,
          comment: newComment,
        },
      },
      onCompleted() {
        setNewComment("");
        // Call refetch if provided (for cohort compatibility)
        if (onRefetch) {
          onRefetch();
        }
      },
    });
  };

  if (!workflowId) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        sx={{
          padding: theme.spacing(3),
          height: "100%",
          minHeight: "200px",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No workflow found
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      sx={{
        padding: theme.spacing(3),
        height: "100%",
        minHeight: "200px",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>

      {/* Input field with submit button */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          multiline
          variant="outlined"
          fullWidth={true}
          value={newComment}
          disabled={loading || disabled}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.metaKey) {
              // CMD + Enter submits the comment
              e.preventDefault();
              handleAddComment();
            }
          }}
          placeholder="Leave a comment"
          size="small"
        />
        <LoadingButton
          onClick={handleAddComment}
          loading={loading}
          variant="contained"
          color="primary"
          size="small"
          disabled={!newComment.trim() || disabled}
          sx={{
            minWidth: "80px",
            fontSize: "0.75rem",
            textTransform: "uppercase",
          }}
        >
          {buttonText}
        </LoadingButton>
      </Stack>

      {comments && comments.length > 0 && <CommentList comments={comments} />}
    </Stack>
  );
}

// Backward compatibility wrapper for the FulfillmentDetails page
export function Comments() {
  const { data: report, isUsingFacility } = useFulfillmentDetails();

  const workflowId = report?.tierIIReport.currentWorkflow?.id;
  const comments = report?.tierIIReport.comments;
  const reportId = report?.tierIIReport.id;

  return (
    <WorkflowComments
      workflowId={workflowId}
      comments={comments}
      reportId={reportId}
      buttonText="Comment"
      title="Workflow Comments"
    />
  );
}
